import { Container, Text } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import type { ScratchModel } from '../../model/scratch/ScratchModel';
import type { PlayerProfile } from '../../model/profile/PlayerProfile';
import { BalanceDisplay } from '../../widgets/BalanceDisplay';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface ScratchHudCallbacks {
  onClaim: () => void;
}

/**
 * Scratch HUD: balance counter (tied to the shared profile so coin
 * credits tick the wallet live), cells-remaining hint above the grid,
 * outcome label + CLAIM button below. CLAIM is hidden until the
 * model emits {@code scratch-complete}.
 */
export class ScratchHudComponent extends Container implements SceneComponent {
  private readonly balance: BalanceDisplay;
  private readonly cellsLabel: Text;
  private readonly outcomeLabel: Text;
  private readonly gemsLabel: Text;
  private readonly claimButton: PrimaryButton;
  private readonly subscriptions: Disposable[] = [];

  constructor(
    private readonly model: ScratchModel,
    private readonly profile: PlayerProfile,
    private readonly callbacks: ScratchHudCallbacks,
    accentColor: number,
  ) {
    super();
    this.balance = new BalanceDisplay(this.profile.balance);
    this.cellsLabel = this.buildText(
      `tap each cell · ${this.model.cellCount} hidden`,
      14,
      0xffffff,
      0.85,
    );
    this.outcomeLabel = this.buildText('', 16, 0xffffff, 0.85);
    this.gemsLabel = this.buildText('', 13, 0x9be7ff, 0.95);
    this.claimButton = new PrimaryButton({
      label: 'CLAIM',
      width: 200,
      fillActive: accentColor,
      fillHover: darken(accentColor, 0.7),
      textColor: 0xffffff,
      onTap: () => this.callbacks.onClaim(),
    });
    this.claimButton.visible = false;

    this.addChild(
      this.balance,
      this.cellsLabel,
      this.outcomeLabel,
      this.gemsLabel,
      this.claimButton,
    );

    this.subscriptions.push(
      this.model.events.on('cell-revealed', (event) => {
        this.cellsLabel.text =
          event.cellsRemaining > 0
            ? `${event.cellsRemaining} cell${event.cellsRemaining === 1 ? '' : 's'} left`
            : 'final reveal';
      }),
      this.model.events.on('scratch-complete', (event) => {
        this.cellsLabel.text = '';
        if (event.outcome === 'lose') {
          this.outcomeLabel.text = 'no match — better luck next time';
          this.claimButton.setLabel('BACK TO MAP');
        } else {
          const matchGlyph = event.matchedSymbol?.glyph ?? '';
          this.outcomeLabel.text =
            `${matchGlyph} ${matchGlyph} ${matchGlyph}  ·  +${event.coins.toLocaleString()}`;
          if (event.gems > 0) {
            this.gemsLabel.text = `+${event.gems} 💎`;
          }
        }
        this.claimButton.visible = true;
      }),
      this.profile.events.on('balance-changed', (event) => {
        this.balance.setAmount(event.after);
        this.balance.bump();
      }),
    );
  }

  layout(ctx: SceneLayoutContext): void {
    const { centerX, machineTop, machineBottom, spinButtonY } = ctx;
    this.balance.x = centerX;
    this.balance.y = Math.max(16, machineTop - 96);
    this.cellsLabel.x = centerX;
    this.cellsLabel.y = Math.max(this.balance.y + 56, machineTop - 24);
    this.outcomeLabel.x = centerX;
    this.outcomeLabel.y = machineBottom + 28;
    this.gemsLabel.x = centerX;
    this.gemsLabel.y = machineBottom + 52;
    this.claimButton.x = centerX;
    this.claimButton.y = spinButtonY;
  }

  destroy(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
    this.balance.destroyDisplay();
    super.destroy({ children: true });
  }

  private buildText(text: string, size: number, fill: number, alpha = 1): Text {
    const t = new Text({
      text,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: size,
        fontWeight: '700',
        fill,
        align: 'center',
      },
    });
    t.anchor.set(0.5);
    t.alpha = alpha;
    return t;
  }
}

/** Multiply each RGB channel by {@code factor} ∈ [0,1] to produce a darker
 * variant of {@code color} for hover-state fill. Cheap and dependency-free. */
function darken(color: number, factor: number): number {
  const r = Math.round(((color >> 16) & 0xff) * factor);
  const g = Math.round(((color >> 8) & 0xff) * factor);
  const b = Math.round((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}
